import { z } from 'zod';
/**
 * Feature modules. Each is a row per tenant in the `tenant_features` table.
 * Off means hidden everywhere: the server guard returns 404 and the client renders nothing.
 */
export const FeatureKey = z.enum([
    'chat',
    'tasks',
    'support',
    'time',
    'invoicing',
    'repos',
    'reports',
    'portal',
    'chapi',
    'todo',
    /** Calendars in My day (docs/adr/0106): a person's own Google, Outlook and iCloud events, read-only. */
    'calendars',
    'catchups',
    'showcase',
    'mcp',
    /** Project rules (docs/adr/0109): when a task moves, Chapsi does the next step. */
    'rules',
]);
export const FEATURES = {
    chat: {
        key: 'chat',
        label: 'Chat',
        description: "Channels per client and project, replies, DMs, mentions. Always on; it's the heart of Chapsi.",
        alwaysOn: true,
        defaultOn: true,
        availability: 'ga',
    },
    tasks: {
        key: 'tasks',
        label: 'Tasks & boards',
        description: 'Create tasks from messages, board and list views, subtasks, priorities, due dates.',
        defaultOn: true,
        availability: 'ga',
    },
    support: {
        key: 'support',
        label: 'Support desk',
        description: 'Email-to-ticket, SLAs, client replies in the channel, a support lane on every board.',
        requires: ['tasks'],
        defaultOn: false,
        availability: 'ga',
    },
    time: {
        key: 'time',
        label: 'Time tracking',
        description: 'Log hours against tasks, retainers per project, monthly reports to the client.',
        requires: ['tasks'],
        defaultOn: false,
        availability: 'ga',
    },
    invoicing: {
        key: 'invoicing',
        label: 'Invoicing',
        description: 'Turn logged hours and fixed-price jobs into invoices. Xero and Stripe sync.',
        requires: ['time'],
        defaultOn: false,
        availability: 'beta',
    },
    repos: {
        key: 'repos',
        label: 'Repositories',
        description: 'Bitbucket, GitHub and GitLab: pushes, PRs and deploys show in the project chat.',
        requires: ['tasks'],
        defaultOn: false,
        availability: 'ga',
    },
    reports: {
        key: 'reports',
        label: 'Reports',
        description: 'Hours, retainer burn, throughput, support SLAs and team workload across clients, with CSV export.',
        // Every report but Time reads work items (docs/adr/0053-reports.md).
        requires: ['tasks'],
        defaultOn: false,
        availability: 'beta',
    },
    portal: {
        key: 'portal',
        label: 'Client portal',
        description: 'Clients see their tasks, tickets and hours. No login to Slack required.',
        defaultOn: false,
        availability: 'beta',
    },
    chapi: {
        key: 'chapi',
        label: 'Chapi assistant',
        description: 'Summarise conversations, draft replies, turn a message into a task with the right fields.',
        defaultOn: false,
        availability: 'beta',
    },
    todo: {
        key: 'todo',
        label: 'My day',
        description: "A short personal to-do list for each person: tasks due today, things from chat, and their own items. Private to each person, admins included.",
        defaultOn: true,
        availability: 'ga',
    },
    calendars: {
        key: 'calendars',
        label: 'Calendars in My day',
        description: "Each person can show their own Google, Outlook or iCloud events beside their to-dos, read-only. Private to each person, admins included.",
        requires: ['todo'],
        defaultOn: true,
        availability: 'beta',
    },
    catchups: {
        key: 'catchups',
        label: 'Catch-ups',
        description: 'Talk and share your screen live in any channel, with up to six people. Audio goes straight between people, never through Chapsi.',
        defaultOn: true,
        availability: 'beta',
    },
    showcase: {
        key: 'showcase',
        label: 'Showcase',
        description: 'Upload designs to a project, turn them into a clickable prototype and play it through. A paid add-on.',
        defaultOn: false,
        availability: 'beta',
        addOn: true,
    },
    mcp: {
        key: 'mcp',
        label: 'Claude connector (MCP)',
        description: 'People make personal tokens so Claude can add and update tasks and estimates as them. Switched in Settings → Connectors.',
        defaultOn: false,
        availability: 'beta',
        ownPage: 'connectors',
    },
    rules: {
        key: 'rules',
        label: 'Rules',
        description: 'When a task moves, Chapsi does the next step: tell the tester, create the deploy task, post in the project chat. Project roles say who.',
        requires: ['tasks'],
        defaultOn: true,
        availability: 'beta',
    },
};
/** Features a preset never switches on or off: paid add-ons (docs/adr/0065) and those switched on their own page (docs/adr/0098). */
export const keptByPresets = (key) => !!FEATURES[key].addOn || !!FEATURES[key].ownPage;
export const FeaturePreset = z.enum(['small_dev_team', 'agency', 'support_team', 'freelancer']);
export const FEATURE_PRESETS = {
    small_dev_team: ['chat', 'tasks', 'repos', 'chapi', 'todo', 'calendars', 'catchups', 'rules'],
    // The client portal is on for agencies so ticket reply emails carry a link to the ticket (QA, 16 Oct 2026).
    // Invoicing is left off: nothing about money appears until an owner turns it on (docs/adr/0054).
    agency: ['chat', 'tasks', 'support', 'time', 'repos', 'portal', 'chapi', 'todo', 'calendars', 'catchups', 'rules'],
    support_team: ['chat', 'tasks', 'support', 'time', 'chapi', 'todo', 'calendars', 'catchups', 'rules'],
    // One person running their own clients (docs/adr/0060): client channels, time, invoicing, the client portal and
    // reports on their hours. No support desk or repositories: those are team tools, and can be turned on later.
    freelancer: ['chat', 'tasks', 'time', 'invoicing', 'portal', 'reports', 'chapi', 'todo', 'calendars', 'catchups', 'rules'],
};
/** Presets for one person (docs/adr/0060): the workspace is set up without team steps. */
export const SOLO_PRESETS = ['freelancer'];
export const FeatureState = z.object({ key: FeatureKey, enabled: z.boolean() });
export const FeatureMap = z.record(FeatureKey, z.boolean());
export const UpdateFeaturesInput = z.object({
    features: z.array(FeatureState).min(1).optional(),
    preset: FeaturePreset.optional(),
}).refine((v) => (v.features ? 1 : 0) + (v.preset ? 1 : 0) === 1, {
    message: 'send either features or preset',
});
/**
 * Applies a change and resolves dependencies so the result is always consistent:
 * enabling a feature enables what it requires; disabling one disables its dependants.
 * Chat stays on whatever is asked.
 */
export function resolveFeatures(current, changes) {
    const next = { ...current };
    const enable = (key) => {
        if (next[key])
            return;
        next[key] = true;
        for (const dep of FEATURES[key].requires ?? [])
            enable(dep);
    };
    const disable = (key) => {
        if (FEATURES[key].alwaysOn || next[key] === false)
            return;
        next[key] = false;
        for (const def of Object.values(FEATURES)) {
            if (def.requires?.includes(key))
                disable(def.key);
        }
    };
    for (const change of changes)
        (change.enabled ? enable : disable)(change.key);
    for (const def of Object.values(FEATURES)) {
        if (def.alwaysOn)
            next[def.key] = true;
        next[def.key] ??= false;
    }
    return next;
}
export function presetFeatures(preset) {
    const on = new Set(FEATURE_PRESETS[preset]);
    return resolveFeatures(Object.fromEntries(FeatureKey.options.map((k) => [k, false])), [...on].map((key) => ({ key, enabled: true })));
}
export function defaultFeatures() {
    return Object.fromEntries(Object.values(FEATURES).map((d) => [d.key, d.defaultOn || !!d.alwaysOn]));
}
//# sourceMappingURL=features.js.map